import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { Coordinates } from '@/api';
import { getCity } from '@/api';
import type { SubscriptionContextStatus } from '@/views/search/useSubscription.ts';

const distanceOptions = [5, 10, 25, 50, 100];
const earthRadiusKm = 6371;
const minimumDistanceKm = 10;
const maximumCitiesToResolve = 40;

const cityCoordinatesCache = new Map<number, Promise<Coordinates | null>>();

const resolveCityCoordinates = (cityId: number): Promise<Coordinates | null> => {
  const cached = cityCoordinatesCache.get(cityId);
  if (cached) {
    return cached;
  }

  const request = getCity({ path: { cityId } })
    .then((response) => (response.response?.ok && response.data ? { latitude: response.data.latitude, longitude: response.data.longitude } : null))
    .catch(() => null);

  cityCoordinatesCache.set(cityId, request);

  void request.then((coordinates) => {
    if (coordinates === null) {
      cityCoordinatesCache.delete(cityId);
    }
  });

  return request;
};

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const haversineKm = (from: Coordinates, to: Coordinates) => {
  const deltaLatitude = toRadians(to.latitude - from.latitude);
  const deltaLongitude = toRadians(to.longitude - from.longitude);
  const a = Math.sin(deltaLatitude / 2) ** 2 + Math.cos(toRadians(from.latitude)) * Math.cos(toRadians(to.latitude)) * Math.sin(deltaLongitude / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.min(1, Math.sqrt(a)));
};

const pickDistance = (radiusKm: number) => {
  const target = Math.max(radiusKm * 1.15, minimumDistanceKm);
  return distanceOptions.find((option) => option >= target) ?? 100;
};

export function useEditorialSubscriptionContext(getCityIds: () => number[]) {
  const location = ref<Coordinates | null>(null);
  const distance = ref(100);
  const status = ref<SubscriptionContextStatus>('resolving');
  let resolutionSequence = 0;

  const resolveLocation = async () => {
    const sequence = ++resolutionSequence;
    const cityIds = getCityIds().slice(0, maximumCitiesToResolve);

    location.value = null;
    distance.value = 100;
    status.value = 'resolving';

    if (cityIds.length === 0) {
      status.value = 'ready';
      return;
    }

    const resolved = await Promise.all(cityIds.map((cityId) => resolveCityCoordinates(cityId)));
    if (sequence !== resolutionSequence) {
      return;
    }

    const coordinates = resolved.filter((coordinate): coordinate is Coordinates => coordinate !== null);

    if (coordinates.length === 0) {
      status.value = 'error';
      return;
    }

    const centroid: Coordinates = {
      latitude: coordinates.reduce((sum, coordinate) => sum + coordinate.latitude, 0) / coordinates.length,
      longitude: coordinates.reduce((sum, coordinate) => sum + coordinate.longitude, 0) / coordinates.length,
    };

    const maxRadiusKm = coordinates.reduce((max, coordinate) => Math.max(max, haversineKm(centroid, coordinate)), 0);

    location.value = centroid;
    distance.value = pickDistance(maxRadiusKm);
    status.value = 'ready';
  };

  onMounted(() => {
    void resolveLocation();
  });

  watch(getCityIds, () => {
    void resolveLocation();
  });

  onBeforeUnmount(() => {
    resolutionSequence++;
  });

  return { location, distance, status };
}
